<script setup lang="ts">
/**
 * WaitlistForm — pre-launch email capture (INC-105, ADR-057).
 *
 * POSTs to the public `waitlist` Edge Function (service-role write path; the
 * table has RLS on with no policies, so there is no direct client DB write).
 * The gateway still requires the anon key, sent as `apikey` + Bearer.
 *
 * Abuse protection: a hidden honeypot input (`website`) that real users never
 * fill — the function silently absorbs any submission that carries it.
 *
 * The function returns the same 200 for a new signup and a duplicate, so the
 * client shows one friendly success either way (never leaks whether an email is
 * already registered).
 *
 * `primary` (default true) gives the submit the accent fill; the landing's closing
 * form passes false, so the page keeps one accent button at rest (DESIGN §4, B7).
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { callWaitlist } from '@/lib/waitlist'
import AppButton from '@/components/ui/AppButton.vue'
import AppField from '@/components/ui/AppField.vue'

withDefaults(defineProps<{ primary?: boolean }>(), { primary: true })

const { t } = useI18n()
const route = useRoute()

type FormState = 'idle' | 'submitting' | 'success' | 'error'

/** Server error codes the user can act on (INC-137); anything else is the generic retry. */
const ERROR_KEYS: Record<number, string> = {
  400: 'waitlist.errorInvalidEmail',
  429: 'waitlist.errorRateLimited',
}

const email = ref('')
const website = ref('') // honeypot — must stay empty
const state = ref<FormState>('idle')
const errorKey = ref('waitlist.error')
const submitted = ref('') // the trimmed address shown in the success line

/** Acquisition source from `?source=…`, sanitized to an opaque short slug. */
const source = computed<string | null>(() => {
  const raw = route.query.source
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string') return null
  return /^[a-z0-9_-]{1,64}$/.test(value) ? value : null
})

const isSubmitting = computed(() => state.value === 'submitting')

async function onSubmit() {
  if (isSubmitting.value) return
  state.value = 'submitting'
  const address = email.value.trim()

  try {
    const status = await callWaitlist({
      email: address,
      source: source.value,
      website: website.value,
    })
    errorKey.value = ERROR_KEYS[status] ?? 'waitlist.error'
    state.value = status >= 200 && status < 300 ? 'success' : 'error'
    if (state.value === 'success') submitted.value = address
  } catch {
    errorKey.value = 'waitlist.error'
    state.value = 'error'
  }
}
</script>

<template>
  <div class="w-full max-w-[540px]">
    <p
      v-if="state === 'success'"
      class="rounded-control bg-surface-raised px-4 py-3 text-fg-primary"
      role="status"
    >
      {{ t('waitlist.success', { email: submitted }) }}
    </p>

    <form v-else class="flex flex-col gap-3" novalidate @submit.prevent="onSubmit">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div class="sm:flex-1">
          <AppField
            v-model="email"
            :label="t('waitlist.emailLabel')"
            type="email"
            name="email"
            required
            autocomplete="email"
            maxlength="254"
            :placeholder="t('waitlist.emailPlaceholder')"
            :disabled="isSubmitting"
          />
        </div>

        <!--
          Honeypot: hidden from real users (visually gone, off the tab order,
          aria-hidden). Bots that autofill every field trip it and the server
          silently discards the submission.
        -->
        <input
          v-model="website"
          type="text"
          name="website"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
          class="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
        />

        <AppButton
          type="submit"
          :variant="primary ? 'accent' : 'default'"
          :busy="isSubmitting"
          :label="isSubmitting ? t('waitlist.submitting') : t('waitlist.submit')"
          class="shrink-0 px-5"
        />
      </div>

      <p v-if="state === 'error'" class="text-small text-danger" role="alert">
        {{ t(errorKey) }}
      </p>

      <p class="text-small text-fg-secondary">
        {{ t('waitlist.consent') }}
        <RouterLink to="/privacy" class="text-accent-text underline">{{
          t('waitlist.privacyLink')
        }}</RouterLink>
      </p>
    </form>
  </div>
</template>
